/**
 * A class that provides methods to calculate node positions, padding, and margins
 * for a hierarchical layout.
 */
export class NodePositioningService {
    /**
     * Constructs a NodePositioningService instance.
     * @param {PaddingOptions} options - The options object containing controlWidth, controlPathLength, and initialPadding.
     */
    constructor({ controlWidth, controlPathLength, initialPadding } = {}) {
        controlWidth = controlWidth !== null && controlWidth !== void 0 ? controlWidth : 24;
        initialPadding = initialPadding !== null && initialPadding !== void 0 ? initialPadding : 24;
        const controlBorderWidth = 8;
        this.controlPathLength = controlPathLength !== null && controlPathLength !== void 0 ? controlPathLength : 28;
        this.levelWidth = controlWidth / 2 + this.controlPathLength;
        this.firstLevelPadding = initialPadding + controlWidth / 2 - controlBorderWidth;
        this.controlPadding = initialPadding - controlWidth / 2;
    }
    /**
     * Calculates content padding for a given level.
     * @param {number} level - The level number.
     * @returns {number} - The content padding.
     */
    calculateContentPadding(level) {
        const mainPadding = this.levelWidth * (level - 1);
        return mainPadding + this.firstLevelPadding;
    }
    /**
     * Calculates control padding for a given level.
     * @param {number} level - The level number.
     * @returns {number} - The control padding.
     */
    calculateControlPadding(level) {
        return this.levelWidth * (level - 1) + this.controlPadding;
    }
    /**
     * Calculates control margin for a given level.
     * @param {number} level - The level number.
     * @returns {number} - The control margin.
     */
    calculateControlMargin(level) {
        return this.levelWidth * (level - 1);
    }
    /**
     * Calculates the left position of a path's starting point for a given level.
     * @param {number} level - The level number.
     * @returns {number} - The path starting point's left position.
     */
    calculatePathStageLeft(level) {
        return (level > 2 ? this.levelWidth * (level - 2) : 0) + 1; // +1 to compensate for the 1px border
    }
    /**
     * Calculates the right position of a path's ending point for a given level.
     * @param {number} level - The level number.
     * @returns {number} - The path ending point's right position.
     */
    calculatePathStageRight(level) {
        return this.calculatePathStageLeft(level) + this.controlPathLength - 1; // -1 to compensate for the 1px border
    }
    /**
     * Calculates the position of an outgoing path for a given level.
     * @param {number} level - The level number.
     * @returns {number} - The outgoing path position.
     */
    calculateOutgoingPathPosition(level) {
        return this.levelWidth * (level - 1);
    }
}
